<script setup lang="ts">
import { computed } from 'vue';
import { useAudioStore } from '../../stores/audioStore';

defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const audioStore = useAudioStore();

// Convert effect values to slider ranges
const pitchValue = computed({
  get: () => audioStore.effects.pitch,
  set: (val: number) => audioStore.setPitch(val),
});

const reverbValue = computed({
  get: () => audioStore.effects.reverb * 100,
  set: (val: number) => audioStore.setReverb(val / 100),
});

const speedValue = computed({
  get: () => audioStore.effects.speed,
  set: (val: number) => audioStore.setSpeed(val),
});

const volumeValue = computed({
  get: () => audioStore.effects.volume,
  set: (val: number) => audioStore.setVolume(val),
});

function formatPitch(val: number): string {
  return val > 0 ? `+${val}` : String(val);
}
</script>

<template>
  <!-- Mobile Drawer -->
  <Teleport to="body">
    <Transition name="drawer">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[100] md:hidden"
        @click.self="emit('close')"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="emit('close')"></div>
        
        <!-- Drawer -->
        <div class="absolute bottom-0 left-0 right-0 bg-console-panel rounded-t-3xl p-6 pb-10 border-t border-console-border">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-bold">Audio Effects</h3>
            <button
              @click="emit('close')"
              class="size-8 flex items-center justify-center rounded-full bg-console-border text-slate-400 hover:text-white"
            >
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <div class="space-y-6">
            <!-- Pitch -->
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-slate-400">Pitch</span>
                <span class="text-primary font-mono">{{ formatPitch(pitchValue) }} st</span>
              </div>
              <input
                type="range"
                v-model.number="pitchValue"
                min="-12"
                max="12"
                step="1"
                class="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <!-- Reverb -->
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-slate-400">Reverb</span>
                <span class="text-primary font-mono">{{ Math.round(reverbValue) }}%</span>
              </div>
              <input
                type="range"
                v-model.number="reverbValue"
                min="0"
                max="100"
                step="1"
                class="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <!-- Speed -->
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-slate-400">Speed</span>
                <span class="text-primary font-mono">{{ speedValue.toFixed(1) }}x</span>
              </div>
              <input
                type="range"
                v-model.number="speedValue"
                min="0.5"
                max="2"
                step="0.1"
                class="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <!-- Volume -->
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-slate-400">Volume</span>
                <span class="text-primary font-mono">{{ volumeValue }} dB</span>
              </div>
              <input
                type="range"
                v-model.number="volumeValue"
                min="-60"
                max="0"
                step="1"
                class="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <!-- Reset Button -->
            <button
              @click="audioStore.resetEffects()"
              class="w-full py-3 bg-console-border text-slate-300 rounded-lg font-bold hover:bg-slate-700 transition-colors"
            >
              Reset to Default
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.drawer-enter-active,
.drawer-leave-active {
  transition: all 0.3s ease;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from > div:last-child,
.drawer-leave-to > div:last-child {
  transform: translateY(100%);
}
</style>
